import React from 'react';

// Real quadratic map z → z² + c from z₀ = 0, clamped so the escaping orbit stays on the chart
function orbit(c: number, steps: number) {
  const out: number[] = [];
  let z = 0;
  for (let i = 0; i <= steps; i++) {
    out.push(z);
    z = z * z + c;
    if (z > 10) z = 10;
  }
  return out;
}
const STEPS = 12;
const W = 560;
const H = 260;
const PAD = {
  l: 44,
  r: 16,
  t: 16,
  b: 34
};
const Y_MIN = -0.6;
const Y_MAX = 2;
const x = (i: number) => PAD.l + i / STEPS * (W - PAD.l - PAD.r);
const y = (v: number) => PAD.t + (1 - (Math.min(Y_MAX, v) - Y_MIN) / (Y_MAX - Y_MIN)) * (H - PAD.t - PAD.b);

/** Figure 1 — a bounded and an escaping orbit, drawn as a plain line chart in the paper's ink. */
export function OrbitFigure() {
  const bounded = orbit(-0.4, STEPS);
  const escaping = orbit(0.3, STEPS);
  const path = (vals: number[]) => vals.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const ticksY = [-0.5, 0, 0.5, 1, 1.5, 2];
  return <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Line chart of two orbits of the quadratic map" data-id="element-299" data-mp="3t21f4wi3kxui">
      {/* Axes + light grid */}
      {ticksY.map(t => <g key={t} data-id="element-300" data-mp="3k24k8d5472r0">
          <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} className="stroke-ink/[0.08]" strokeWidth={1} data-id="element-301" data-mp="3k2mljo6mw75s" />
          <text x={PAD.l - 10} y={y(t) + 3.5} textAnchor="end" className="fill-ink/45 font-accent text-[10px]" data-id="element-302" data-mp="3p1qx2lh16gza">
            {t}
          </text>
        </g>)}
      <line x1={PAD.l} x2={PAD.l} y1={PAD.t} y2={H - PAD.b} className="stroke-ink/40" strokeWidth={1} data-id="element-303" data-mp="kmhnu30zlpi3" />
      <line x1={PAD.l} x2={W - PAD.r} y1={H - PAD.b} y2={H - PAD.b} className="stroke-ink/40" strokeWidth={1} data-id="element-304" data-mp="6vupwd31w3n2" />
      {Array.from({
      length: STEPS + 1
    }, (_, i) => i % 2 === 0 ? <text key={i} x={x(i)} y={H - PAD.b + 18} textAnchor="middle" className="fill-ink/45 font-accent text-[10px]" data-id="element-305" data-mp="zka3qwag4fwt">
            {i}
          </text> : null)}
      <text x={W - PAD.r} y={H - 4} textAnchor="end" className="fill-ink/55 font-accent text-[10px]" data-id="element-306" data-mp="14cjolxwpeps8">
        n
      </text>

      {/* Orbits */}
      <path d={path(escaping)} fill="none" className="stroke-ink/55" strokeWidth={1.25} strokeDasharray="4 4" data-id="element-307" data-mp="197ck6826uimh" />
      <path d={path(bounded)} fill="none" className="stroke-ink" strokeWidth={1.4} data-id="element-308" data-mp="147858mgxhvyd" />
      {bounded.map((v, i) => <rect key={i} x={x(i) - 2} y={y(v) - 2} width={4} height={4} className="fill-ink" data-id="element-309" data-mp="z96mk2nezynf" data-mpi={i} />)}

      {/* Legend */}
      <g transform={`translate(${PAD.l + 16} ${PAD.t + 10})`} className="font-accent text-[10px]" data-id="element-310" data-mp="v9o37bb8rvmp">
        <line x1={0} x2={22} y1={0} y2={0} className="stroke-ink" strokeWidth={1.4} data-id="element-311" data-mp="qfa0soy5wo8j" />
        <text x={30} y={3.5} className="fill-ink/70" data-id="element-312" data-mp="lfxgaaurjt94">c = −0.4</text>
        <line x1={0} x2={22} y1={16} y2={16} className="stroke-ink/55" strokeWidth={1.25} strokeDasharray="4 4" data-id="element-313" data-mp="gk9rhwk71245" />
        <text x={30} y={19.5} className="fill-ink/70" data-id="element-314" data-mp="bjcc1rqkim6c">c = 0.3</text>
      </g>
    </svg>;
}